import { Card, Divider, Pagination, message, Button } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import SearchFilter from '../../component/Filter';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import baseuri from '../../methods/baseuri';
import {
	getBlockchainNetworks,
	getCurrencies,
} from '../../methods/redux/reducers/currency';
import { getInCreditTransactions } from '../../methods/redux/reducers/transactions';
import { getUserProfileById } from '../../methods/redux/reducers/user-profile';
import Aggregate from './Aggregate';
import InCreditItem from './InCreditItem';
import DepositFilters from './DepositFilters';
import NativeNumber from '../../component/Numeral';
import remakeQueryString from '../../methods/utils/remake-query-string';

export const UsersListPage = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;

	& .myCard {
		border-radius: 10px;
		gap: 1em;
	}
`;
export const MainSection = styled(Card)`
	border-radius: 10px;
	gap: 2em;
`;
const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const InCreditTransactions = () => {
	const dispatch = useAppDispatch();
	const { userId } = useParams();

	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([] as any);

	const [searchParams, setSearchParams] = useSearchParams();
	const { user } = useAppSelector((store) => store.userProfile);
	const { inCredit } = useAppSelector((store) => store.transactions);
	const { currencies, blockchains } = useAppSelector(
		(store) => store.currencies
	);

	const getQueryParams = () => Object.fromEntries(searchParams.entries());
	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(inCredit.paginator.perPage),
			});
		}

		if (userId) {
			dispatch(getUserProfileById(userId));
		}

		dispatch(getCurrencies({}));
		dispatch(getBlockchainNetworks());
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			if (userId) {
				dispatch(getInCreditTransactions({ ...queryData, userId }));
			} else {
				dispatch(getInCreditTransactions(queryData));
			}
		}
	}, [searchParams]);

	const currencySelect = currencies.map((item: any) => ({
		name: item.symbol,
		value: item._id,
	}));

	const blockchainSelect = blockchains.map((item: any) => ({
		name: item.abbreviation,
		value: item._id,
	}));

	const providerKeySelect = [
		{ name: 'FIDELITY', value: 'FIDELITY' },
		{ name: 'BELLBANK', value: 'BELLBANK' },
		{ name: 'BLOCKCHAIN', value: 'BLOCKCHAIN' },
		{ name: 'PALMPAY', value: 'PALMPAY' },
		{ name: 'SAFE_HAVEN', value: 'SAFE_HAVEN' },
	];

	// Transform the results data to match the headers
	const transformSwapRequests = (requests: any[]) =>
		requests?.map((data: any) => ({
			title: data.title,
			transactionAmount: `${data.transactionAmount} ${data.currencyId?.symbol}`,
			customer: data.userId?.username,
			amountUsd: data?.transactionAmountInUSD,
			blockchain: data.blockchainId?.abbreviation,
			transactionHash: `https://${data.blockchainId?.explorerUrl}/tx/${data.transactionHash}`,
			transactionSource: data.transactionSource,
			blockchainValidation: data.blockchainValidation,
			providerKey: data.providerKey,
			fundsRemitted: data.fundsRemitted ? 'Remitted' : 'Not Yet Remitted',
			status: data.status,
			cronTouch: data.cronTouch ? 'True' : 'False',
			settlementAmount: data.settlementAmount,
			depositFee:
				Number(data?.transactionAmount) - Number(data?.settlementAmount),
			createdAt: moment(data.createdAt).format('MMM DD, YY / hh:mm:ss a'), // Handle formatted date
			updatedAt: moment(data.updatedAt).format('MMM DD, YY / hh:mm:ss a'), // Handle formatted date
			lastUpdated: data.lastUpdatedByUserForm,
		}));

	// Define headers with simple keys
	const headers = [
		{ label: 'Title', key: 'title' },
		{ label: 'Transaction Amount', key: 'transactionAmount' },
		{ label: 'Customer', key: 'customer' },
		{ label: 'Amount(Usd)', key: 'amountUsd' },
		{ label: 'Blockchain', key: 'blockchain' },
		{ label: 'Transaction Hash', key: 'transactionHash' },
		{ label: 'Transaction Source', key: 'transactionSource' },
		{ label: 'Blockchain Validation', key: 'blockchainValidation' },
		{ label: 'Provider Key', key: 'providerKey' },
		{ label: 'Funds Remitted', key: 'fundsRemitted' },
		{ label: 'Status', key: 'status' },
		{ label: 'Cron Touch', key: 'cronTouch' },
		{ label: 'Settlement Amount', key: 'settlementAmount' },
		{ label: 'Deposit Fee', key: 'depositFee' },
		{ label: 'Created At', key: 'createdAt' },
		{ label: 'Updated At', key: 'updatedAt' },
		{ label: 'Last Updated', key: 'lastUpdated' },
	];

	const handleDownload = () => {
		setLoading(true);
		const params1 = getQueryParams();

		setResults([]);
		baseuri
			.get('/report/wallet/deposits' || '', {
				params: {
					...params1,
					perPage: inCredit?.paginator?.itemCount || 0,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawData = res.data.data.itemsList;
				const transformedData = transformSwapRequests(rawData);
				setResults(transformedData);
				setLoading(false);
			})
			.catch(() => {
				message.error('could not download CSV');
				setLoading(false);
			});
	};

	useEffect(() => {
		if (results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results]);

	return (
		<Card>
			<h1>
				InCredit Transactions{' '}
				{userId && (
					<span>
						{' '}
						for{' '}
						<Link
							title="View Profile"
							to={`/user-profile/${user.profile?._id}`}
						>
							@{user.profile?.username}
						</Link>
					</span>
				)}
			</h1>
			<p>All the deposit of funds from an external wallet address.</p>

			<Aggregate data={inCredit.result} />

			{!userId && <DepositFilters />}
			<br />

			<SearchFilter
				title="Search InCredit Transaction Records"
				searchFilters={[
					{
						label: 'Currency',
						name: 'currencyId',
						htmlElement: 'select',
						selectOptions: currencySelect,
					},
					{
						label: 'Blockchain Network',
						name: 'blockchainId',
						htmlElement: 'select',
						selectOptions: blockchainSelect,
					},
					{
						label: 'Provider Key',
						name: 'providerKey',
						htmlElement: 'select',
						selectOptions: providerKeySelect,
					},
					{
						label: 'Start Date',
						name: 'startDate',
						htmlElement: 'datepicker',
					},
					{
						label: 'End Date',
						name: 'endDate',
						htmlElement: 'datepicker',
					},
					{
						label: 'Status',
						name: 'status',
						defaultSelectedValue: 'active',
						htmlElement: 'select',
						selectOptions: [
							{ name: 'Pending', value: 'pending' },
							{ name: 'Active', value: 'active' },
							{ name: 'Deleted', value: 'deleted' },
						],
					},
					{
						label: 'End Date',
						name: 'endDate',
						htmlElement: 'datepicker',
					},
					{
						label: 'User Id',
						name: 'userId',
						htmlElement: 'input',
					},
					{
						label: 'Stranger Deposit',
						name: 'strangerDeposit',
						htmlElement: 'select',
						selectOptions: [
							{ name: 'True', value: true },
							{ name: 'False', value: false },
						],
					},
					{
						label: 'Stranger Deposit Refunded',
						name: 'strangerDepositRefunded',
						htmlElement: 'select',
						selectOptions: [
							{ name: 'True', value: true },
							{ name: 'False', value: false },
						],
					},
					{
						label: 'Transaction Hash/Session ID',
						name: 'transactionHash',
						htmlElement: 'input',
					},
				]}
				retrieveQueryString={updateQueryString}
			/>

			<br />

			<p>
				Total of{' '}
				<strong>
					<NativeNumber value={inCredit.paginator.itemCount} /> items
				</strong>{' '}
				found
			</p>

			<br />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={inCredit.paginator.itemCount}
				current={inCredit.paginator.currentPage}
				pageSize={inCredit.paginator.perPage}
			/>

			{inCredit && inCredit.itemsList?.length > 0 && (
				<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<DownloadButton
						danger
						loading={loading}
						icon={<DownloadOutlined />}
						onClick={() => handleDownload()}
					>
						{loading ? 'Loading csv...' : 'Download CSV'}
					</DownloadButton>
					<CSVLink
						data={results}
						headers={headers}
						className="download"
						filename="In-creditTransactions.csv"
						ref={csvLinkRef}
					/>
				</div>
			)}

			<br />

			{inCredit.itemsList.map((item: any) => (
				<InCreditItem key={item?._id} data={item} />
			))}

			<Divider />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={inCredit.paginator.itemCount}
				current={inCredit.paginator.currentPage}
				pageSize={inCredit.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>
		</Card>
	);
};

export default InCreditTransactions;
